<template>
	<div class="substance-form">
		<div class="mx-5">
			<v-form ref="generalForm" v-model="generalFormValid" lazy-validation>
				<v-row no-gutters v-if="admin">
					<b v-translate>Language:</b>
					<v-btn-toggle class="ml-2" v-model="language" mandatory>
						<v-btn value="pt" small class="text-overline" key="pt">
							<strong class="mr-1">PT</strong>
							<span v-translate>Portuguese</span>
						</v-btn>
						<v-btn value="en" small class="text-overline" key="en">
							<strong class="mr-1">EN</strong>
							<span v-translate>English</span>
						</v-btn>
						<v-btn value="es" small class="text-overline" key="es">
							<strong class="mr-1">ES</strong>
							<span v-translate>Spanish</span>
						</v-btn>
					</v-btn-toggle>
					<v-spacer></v-spacer>
					<v-alert v-model="success" color="alert" type="success">
						<translate>Saved Successfully</translate>
					</v-alert>
					<v-alert v-model="error" color="red" type="error">
						<translate>Error</translate>
					</v-alert>
				</v-row>
				<template v-else>
					<v-alert v-model="success" color="alert" type="success" class="ml-3 mt-3">
						<translate>Saved Successfully</translate>
					</v-alert>
					<v-alert v-model="error" color="red" type="error" class="ml-3 mt-3">
						<translate>Error</translate>
					</v-alert>
				</template>
				<v-row no-gutters v-if="admin">
					<v-checkbox
						v-model="product.revision"
						color="primary"
						:label="$gettext('Rever')"
						class="mt-0 pb-0"
						hide-details
					/>
					<v-btn
						small
						class="text-overline mt-1 ml-1"
						color="primary"
						@click="showImportProduct = !showImportProduct"
						:loading="saving"
					>
						<span v-translate>Import Substance</span>
					</v-btn>
					<v-row v-if="showImportProduct" no-gutters class="mt-2">
						<v-col cols="8" class="px-2">
							<SearchSubstances v-if="importSubstanceType == 'mtc'" type="formula" method="get" @click="importProduct" />
							<SearchProducts v-else context="new-product" :substance="true" type="supplement" method="get" @click="importProduct" />
						</v-col>
						<v-col cols="4" class="pl-2">
							<v-radio-group v-model="importSubstanceType" row>
								<v-radio value="mtc" :label="$gettext('TCM')"/>
								<v-radio value="fito" :label="$gettext('Fitotherapy')"/>
							</v-radio-group>
						</v-col>
					</v-row>
				</v-row>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate>General</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col cols="12" md="4">
										<v-text-field
											v-if="admin"
											:rules="requiredRule"
											v-model="product.information[language].name"
											:label="$gettext('Name')"
											outlined
											class="px-2"
										></v-text-field>
										<v-text-field
											v-else
											:rules="requiredRule"
											v-model="product.name"
											:label="$gettext('Name')"
											outlined
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-model="product.pinyin_name"
											:label="$gettext('Pinyin Name')"
											outlined
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-model="product.chinese_name"
											:label="$gettext('Chinese Name')"
											outlined
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-model="product.latin_name"
											:label="$gettext('Latin Name')"
											outlined
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-if="admin"
											v-model="product.information[language].channels"
											:label="$gettext('Channels')"
											outlined
											class="px-2"
										></v-text-field>
										<v-text-field
											v-else
											v-model="product.channels"
											:label="$gettext('Channels')"
											outlined
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-if="admin"
											v-model="product.information[language].properties"
											:label="$gettext('Properties')"
											outlined
											class="px-2"
										></v-text-field>
										<v-text-field
											v-else
											v-model="product.properties"
											:label="$gettext('Properties')"
											outlined
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-model="product.temperature"
											:label="$gettext('Temperature')"
											outlined
											class="px-2"
											:rules="intRules"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-if="admin"
											v-model="product.information[language].toxicity_overdose"
											:label="$gettext('Toxicity & Overdose')"
											outlined
											class="px-2"
										></v-text-field>
										<v-text-field
											v-else
											v-model="product.toxicity_overdose"
											:label="$gettext('Toxicity & Overdose')"
											outlined
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field v-if="admin" v-model="product.information[language].notes" :label="$gettext('Notes')" outlined class="px-2"></v-text-field>
										<v-text-field v-else v-model="product.notes" :label="$gettext('Notes')" outlined class="px-2"></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-if="admin"
											v-model="product.information[language].actions"
											:label="$gettext('Actions')"
											outlined
											class="px-2"
										></v-text-field>
										<v-text-field
											v-else
											v-model="product.actions"
											:label="$gettext('Actions')"
											outlined
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-model="product.min_dosage"
											:label="$gettext('Min. Dosage')"
											outlined
											class="px-2"
											:rules="floatRules"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="4">
										<v-text-field
											v-model="product.max_dosage"
											:label="$gettext('Max. Dosage')"
											outlined
											class="px-2"
											:rules="floatRules"
										></v-text-field>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate>Indications</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col v-if="admin" cols="12" sm="12" class="px-2">
                    <v-textarea
                      outlined
                      :placeholder="$gettext('Write the substance indications')"
                      v-model="product.information[language].indications"
                    ></v-textarea>
									</v-col>
									<v-col cols="12" sm="12" class="mt-4 px-2">
										<SearchICD :selectedICD="product.indications_icd" context="new-product" />
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate>Contraindications</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col v-if="admin" cols="12" sm="12" class="px-2">
                    <v-textarea
                      outlined
                      :placeholder="$gettext('Write the substance contraindications')"
                      v-model="product.information[language].contraindications"
                    ></v-textarea>
									</v-col>
									<v-col cols="12" sm="12" class="mt-4 px-2">
										<SearchICD :selectedICD="product.contraindications_icd" context="new-product" />
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate>Drug Interactions</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col v-if="admin" cols="12" sm="12" class="px-2">
                    <v-textarea
                      outlined
                      :placeholder="$gettext('Write the substance interactions')"
                      v-model="product.information[language].interactions"
                    ></v-textarea>
									</v-col>
									<v-col cols="12" sm="12" class="mt-4 px-2">
										<SearchDrugs :selectedDrugs="product.drugs" :drugInteractions="drugInteractions" context="new-product" />
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" sm="12">
						<v-btn v-if="edit" color="primary" class="float-right ml-2" @click="editProduct" :loading="saving">
							<translate>Save</translate>
						</v-btn>
						<v-btn v-else color="primary" class="float-right ml-2" @click="createProduct" :loading="saving">
							<translate>Create</translate>
						</v-btn>
						<v-btn color="primary" class="float-right" :to="{name: 'cmm'}">
							<translate>Cancel</translate>
						</v-btn>
					</v-col>
				</v-row>
			</v-form>
		</div>
	</div>
</template>

<script>
import SearchICD from "@/components/icd/SearchICD.vue";
import SearchDrugs from "@/components/drugs/SearchDrugs.vue";
import SearchSubstances from "@/components/products/SearchSubstances.vue";
import SearchProducts from "@/components/products/SearchProducts.vue";
import axios from "axios";

export default {
	name: "SubstanceForm",
	props: {
		product: {
			default: () => ({
				name: null,
				revision: false,
				pinyin_name: null,
				chinese_name: null,
				latin_name: null,
				channels: null,
				properties: null,
				temperature: null,
				toxicity_overdose: null,
				notes: null,
				actions: null,
				min_dosage: null,
				max_dosage: null,
				indications_icd: [],
				contraindications_icd: [],
				drugs: [],
				information: {
					pt: {
						language: "pt",
						name: null,
						channels: null,
						properties: null,
						toxicity_overdose: null,
						notes: null,
						actions: null,
						indications: null,
						contraindications: null,
						interactions: null
					},
					en: {
						language: "en",
						name: null,
						channels: null,
						properties: null,
						toxicity_overdose: null,
						notes: null,
						actions: null,
						indications: null,
						contraindications: null,
						interactions: null
					},
					es: {
						language: "es",
						name: null,
						channels: null,
						properties: null,
						toxicity_overdose: null,
						notes: null,
						actions: null,
						indications: null,
						contraindications: null,
						interactions: null
					}
				}
			})
		},
		drugInteractions: {
			default: () => []
		},
		edit: {
			default: false
		},
		admin: {
			default: false
		}
	},
	components: {
		SearchICD,
		SearchDrugs,
		SearchSubstances,
		SearchProducts
	},
	data: () => ({
		language: "pt",
		saving: false,
		generalFormValid: false,
		success: false,
		error: false,
		showImportProduct: false,
		importSubstanceType: "mtc"
	}),
	methods: {
		editProduct() {
			this.success = false;
			this.error = false;
			if (this.validateForms()) {
				this.saving = true;
				const form = this.getForm();
				const productId = this.$route.params.sid;
				axios
					.post("/mtc/substances/" + productId + "/edit", form)
					.then(response => {
						console.log(response.data);
						this.saving = false;
						window.scrollTo(0, 0);
						if (response.data === "OK") {
							this.success = true;
						} else {
							this.error = true;
						}
					})
					.catch(error => {
						window.scrollTo(0, 0);
						this.saving = false;
						this.error = true;
						console.log(error);
					});
			} else {
				window.scrollTo(0, 0);
			}
		},
		createProduct() {
			this.success = false;
			this.error = false;
			if (this.validateForms()) {
				console.log("creating");
				this.saving = true;
				const form = this.getForm();
				axios
					.post("/mtc/substances/new", form)
					.then(response => {
						console.log(response.data);
						this.saving = false;
						window.scrollTo(0, 0);
						if (response.data === "OK") {
							this.success = true;
							if (!this.admin) {
								this.$router.push({
									name: "cmm"
								});
							} else {
								console.log("CREATED");
								this.$router.push({
									name: "admin-cmm"
								});
							}
						} else {
							this.error = true;
						}
					})
					.catch(error => {
						window.scrollTo(0, 0);
						this.saving = false;
						this.error = true;
						console.log(error);
					});
			} else {
				window.scrollTo(0, 0);
			}
		},
		validateForms() {
			return this.$refs.generalForm.validate();
		},
		getForm() {
			const formData = new FormData();
			formData.append("name", this.product.name);
			if (this.product.revision) {
				formData.append("revision", true);
			} else {
				formData.append("revision", false);
			}
			if (this.product.pinyin_name != null) {
				formData.append("pinyin_name", this.product.pinyin_name);
			}
			if (this.product.chinese_name != null) {
				formData.append("chinese_name", this.product.chinese_name);
			}
			if (this.product.latin_name != null) {
				formData.append("latin_name", this.product.latin_name);
			}
			if (this.product.channels != null) {
				formData.append("channels", this.product.channels);
			}
			if (this.product.properties != null) {
				formData.append("properties", this.product.properties);
			}
			if (this.product.temperature != null) {
				formData.append("temperature", this.product.temperature);
			}
			if (this.product.toxicity_overdose != null) {
				formData.append("toxicity_overdose", this.product.toxicity_overdose);
			}
			if (this.product.notes != null) {
				formData.append("notes", this.product.notes);
			}
			if (this.product.actions != null) {
				formData.append("actions", this.product.actions);
			}
			if (this.product.min_dosage != null) {
				formData.append("min_dosage", this.product.min_dosage);
			}
			if (this.product.max_dosage != null) {
				formData.append("max_dosage", this.product.max_dosage);
			}

			//Indications
			var indications_icd = [];
			this.product.indications_icd.forEach(icd => {
				indications_icd.push({disease_id: icd.disease_id, degree: icd.degree});
			});
			formData.append("indications_json", JSON.stringify(indications_icd));
			//Contraindications
			var contraindications_icd = [];
			this.product.contraindications_icd.forEach(icd => {
				contraindications_icd.push({disease_id: icd.disease_id, degree: icd.degree});
			});
			formData.append("contraindications_json", JSON.stringify(contraindications_icd));
			//Drugs
			var drugs = [];
			this.product.drugs.forEach(d => {
				drugs.push(d);
			});
			formData.append("drugs_json", JSON.stringify(drugs));
			//Info
			var information = [];
			for (const [key, value] of Object.entries(this.product.information)) {
				console.log(key);
				information.push(value);
			}
			formData.append("information_json", JSON.stringify(information));

			console.log(information, indications_icd, contraindications_icd, drugs);
			return formData;
		},
		isInt(n) {
			console.log(n);
			if (n == 0) return true;
			if (n) {
				return Number(n) === n && n % 1 === 0;
			}
			return false;
		},
		isFloat(n) {
			if (n == 0) return true;
			if (n) {
				return Number(n) === n && n % 1 !== 0;
			}
			return false;
		},
		importProduct(productId) {
			this.saving = true;
			let link;
			if(this.importSubstanceType == 'mtc') link = "/mtc/substances/" + productId + "/edit";
			else link = "/product/" + productId + "/edit";
			axios
				.get(link)
				.then(response => {
					this.saving = false;
					if(this.importSubstanceType == 'mtc') {
						let id = this.product.id;
						this.product = response.data.product;
						this.product.id = id;
					}
					else {
						console.log(response.data);
						this.product.indications_icd = response.data.product.indications_icd;
						this.product.contraindications_icd = response.data.product.contraindications_icd;
						this.product.drugs = response.data.product.drugs;
						this.product.information.pt.indications = response.data.product.information.pt.indications;
						this.product.information.pt.contraindications = response.data.product.information.pt.contraindications;
						this.product.information.pt.interactions = response.data.product.information.pt.interactions;
						this.product.information.en.indications = response.data.product.information.en.indications;
						this.product.information.en.contraindications = response.data.product.information.en.contraindications;
						this.product.information.en.interactions = response.data.product.information.en.interactions;
						this.product.information.es.indications = response.data.product.information.es.indications;
						this.product.information.es.contraindications = response.data.product.information.es.contraindications;
						this.product.information.es.interactions = response.data.product.information.es.interactions;
					}
				})
				.catch(error => {
					console.log(error);
					this.saving = false;
				});
		}
	},
	computed: {
		floatRules() {
			return [
				v =>
					this.isFloat(Number(v)) ||
					this.isInt(Number(v)) ||
					this.$gettext("Invalid quantity")
			];
		},
		intRules() {
			return [v => this.isInt(Number(v)) || this.$gettext("Invalid number")];
		},
		requiredRule() {
			return [v => !!v || this.$gettext("Required")];
		},
		availableLanguages() {
			return [
				{ code: "en", name: "English" },
				{ code: "pt", name: "Português" },
				{ code: "es", name: "Espanol" }
			];
		},
		availableSubstanceTypes() {
			return [
				{ name: this.$gettext("Mineral"), code: "mineral" },
				{ name: this.$gettext("Plant"), code: "plant" },
				{ name: this.$gettext("Vitamin"), code: "vitamin" },
				{ name: this.$gettext("Excipient"), code: "excipient" },
				{ name: this.$gettext("Other"), code: "other" }
			];
		},
		availablePlantParts() {
			return [
				{ name: this.$gettext("Root"), code: "raiz" },
				{ name: this.$gettext("Leaf"), code: "folha" },
				{ name: this.$gettext("Flower"), code: "flor" }
			];
		}
	}
};
</script>